import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { COLLECTIONS, FACETS, TERMS, type Facet } from '@/lib/taxonomy';
import { activeFilterCount, hasFilters, type ArchiveFilters } from '@/lib/archiveFilters';
import { termBySlug } from '@/lib/noteTags';
import { useIsMobile } from '@/hooks/useIsMobile';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

/** How many terms a facet shows before "Show all". */
const FACET_PREVIEW = 8;

/* ───────────────────────────────────────────────────────────────────────────
   Search — a compact field in the toolbar. On wide screens it sits inline; on
   phones it is an icon that opens a field over the category chips.
   ─────────────────────────────────────────────────────────────────────────── */

interface ArchiveSearchProps {
  value: string;
  onChange: (value: string) => void;
  /**
   * Always show the field (no phone icon that opens it). Used in the toolbar,
   * where there is room for it on every screen.
   */
  inline?: boolean;
}

export function ArchiveSearch({ value, onChange, inline = false }: ArchiveSearchProps) {
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  if (inline) {
    return (
      <label className="relative block">
        <span className="sr-only">Search the archive</span>
        <Search
          aria-hidden="true"
          className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground/55"
        />
        {/* 16px type: anything smaller makes iPhones zoom the page on focus. */}
        <input
          type="search"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Search the records"
          autoComplete="off"
          enterKeyHint="search"
          className="h-10 w-full rounded-full border border-foreground/35 bg-card pl-10 pr-4 font-sans text-base text-foreground shadow-[0_1px_2px_rgba(40,25,10,0.1)] placeholder:text-foreground/50 focus-visible:border-foreground/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
        />
      </label>
    );
  }

  // Move focus into the field when it opens on a phone.
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const fieldClass =
    'h-8 w-full rounded-full border border-foreground/25 bg-card pl-8 pr-3 font-sans text-sm text-foreground placeholder:text-foreground/40 focus-visible:border-foreground/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1';

  return (
    <>
      {/* Wide screens: always-visible inline field. */}
      <label className="relative hidden w-44 md:block lg:w-56">
        <span className="sr-only">Search the archive</span>
        <Search
          aria-hidden="true"
          className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-foreground/40"
        />
        <input
          type="search"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Search records"
          autoComplete="off"
          className={fieldClass}
        />
      </label>

      {/* Phones: an icon… */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Search the archive"
        className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-foreground/25 text-foreground transition-colors hover:border-foreground/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 md:hidden"
      >
        <Search aria-hidden="true" className="h-4 w-4" />
        {value.trim() && (
          <span
            aria-hidden="true"
            className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full border-2 border-background bg-foreground"
          />
        )}
      </button>

      {/* …that opens a field laid over the whole toolbar. */}
      {open && (
        <div className="absolute inset-0 z-10 flex items-center gap-2 bg-background px-4 md:hidden">
          <label className="relative min-w-0 flex-1">
            <span className="sr-only">Search the archive</span>
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-foreground/40"
            />
            <input
              ref={inputRef}
              type="search"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape' || e.key === 'Enter') setOpen(false);
              }}
              placeholder="Search records"
              autoComplete="off"
              enterKeyHint="search"
              className={fieldClass}
            />
          </label>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="shrink-0 px-1 font-engraved text-[10px] font-semibold uppercase tracking-[0.2em] text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
          >
            Done
          </button>
        </div>
      )}
    </>
  );
}

/* ───────────────────────────────────────────────────────────────────────────
   Filters — the facet groups (type, subject, place, names, format, provenance)
   in a panel that opens on demand, instead of sitting in the page.
   ─────────────────────────────────────────────────────────────────────────── */

interface FilterSheetProps {
  filters: ArchiveFilters;
  onChange: (next: ArchiveFilters) => void;
  /** Per-term counts that respect the other active filters. */
  counts: Record<string, number>;
  /** Records matching the current filters. */
  resultCount: number;
  /** True until the first results arrive. */
  loading: boolean;
  /** A taller, labelled button to sit beside the inline search field. */
  inline?: boolean;
}

export function FilterSheet({ filters, onChange, counts, resultCount, loading, inline = false }: FilterSheetProps) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<Set<Facet>>(new Set());
  const termCount = filters.terms.length;

  // Terms available per facet: anything with records, plus anything selected
  // (so a chosen term never vanishes just because its count fell to zero).
  const groups = useMemo(() => {
    return FACETS.map((facet) => {
      const terms = TERMS.filter((t) => t.facet === facet.id)
        .map((t) => ({
          slug: t.slug,
          label: t.label,
          count: counts[t.slug] ?? 0,
          selected: filters.terms.includes(t.slug),
        }))
        .filter((t) => t.count > 0 || t.selected)
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
      return { facet, terms };
    }).filter((g) => g.terms.length > 0);
  }, [counts, filters.terms]);

  const toggleTerm = (slug: string) => {
    const terms = filters.terms.includes(slug)
      ? filters.terms.filter((s) => s !== slug)
      : [...filters.terms, slug];
    onChange({ ...filters, terms });
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          className={cn(
            'relative inline-flex shrink-0 items-center transition-[transform,box-shadow,background-color,color] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            inline
              ? 'h-10 justify-center gap-2 rounded-full border border-foreground/35 bg-card px-3.5 font-engraved text-[11px] font-bold uppercase tracking-[0.14em] text-foreground shadow-[0_1px_2px_rgba(40,25,10,0.1)] hover:bg-foreground hover:text-background focus-visible:ring-offset-1'
              : 'h-8 gap-2 rounded-full border border-foreground/25 px-3 font-engraved text-[10px] font-semibold uppercase tracking-[0.2em] text-foreground hover:border-foreground/60 focus-visible:ring-offset-1',
          )}
        >
          <SlidersHorizontal aria-hidden="true" className={inline ? 'h-[18px] w-[18px]' : 'h-3.5 w-3.5'} />
          <span className="hidden sm:inline">Filters</span>
          <span className="sr-only sm:hidden">Filters</span>
          {termCount > 0 && (
            <span
              className={cn(
                'flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-flag-red px-1 font-mono text-[10px] leading-none tabular-nums text-white',
                inline && 'absolute -right-1 -top-1 sm:static',
              )}
            >
              {termCount}
            </span>
          )}
        </button>
      </SheetTrigger>

      <SheetContent
        side={isMobile ? 'bottom' : 'right'}
        className={cn(
          'gap-0 border-foreground/40 bg-background p-0',
          isMobile ? 'max-h-[85svh] rounded-t-3xl' : 'w-full sm:max-w-md',
        )}
      >
        <SheetHeader className="border-b-[3px] border-foreground p-4 pr-12">
          <SheetTitle className="font-engraved text-sm font-semibold uppercase tracking-[0.3em]">
            Filter records
          </SheetTitle>
          <SheetDescription className="font-display text-sm italic text-foreground/70">
            Narrow the current category by subject, place, people, format and more.
          </SheetDescription>
        </SheetHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-4">
          {loading ? (
            <div className="space-y-5 py-5">
              {[0, 1, 2].map((i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-3 w-24" />
                  <div className="flex flex-wrap gap-2">
                    {[...Array(5)].map((_, j) => (
                      <Skeleton key={j} className="h-7" style={{ width: `${64 + ((i * 5 + j * 23) % 50)}px` }} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            groups.map(({ facet, terms }) => {
              const isExpanded = expanded.has(facet.id);
              const visible = isExpanded ? terms : terms.slice(0, FACET_PREVIEW);
              // Never hide a selected term behind "Show all".
              const hiddenSelected = terms.slice(FACET_PREVIEW).filter((t) => t.selected);
              const shown = isExpanded ? visible : [...visible, ...hiddenSelected];
              const activeInFacet = terms.filter((t) => t.selected).length;

              return (
                <fieldset key={facet.id} className="border-b border-foreground/15 py-4">
                  <legend className="sr-only">{facet.label}</legend>
                  <div
                    aria-hidden="true"
                    className="mb-2 flex items-baseline gap-2 font-engraved text-[10px] font-semibold uppercase tracking-[0.2em] text-foreground/60"
                  >
                    {facet.label}
                    {activeInFacet > 0 && (
                      <span className="font-mono tabular-nums text-foreground">{activeInFacet}</span>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {shown.map((t) => (
                      <button
                        key={t.slug}
                        type="button"
                        onClick={() => toggleTerm(t.slug)}
                        aria-pressed={t.selected}
                        className={cn(
                          'inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-left transition-colors',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                          t.selected
                            ? 'border-foreground bg-foreground text-background'
                            : 'border-foreground/25 bg-transparent text-foreground hover:border-foreground/60',
                        )}
                      >
                        <span className="font-display text-[15px] leading-none">{t.label}</span>
                        <span
                          className={cn(
                            'font-mono text-[10px] leading-none tabular-nums',
                            t.selected ? 'text-background/70' : 'text-foreground/50',
                          )}
                        >
                          {t.count.toLocaleString()}
                        </span>
                      </button>
                    ))}

                    {terms.length > FACET_PREVIEW && (
                      <button
                        type="button"
                        onClick={() =>
                          setExpanded((prev) => {
                            const next = new Set(prev);
                            if (next.has(facet.id)) next.delete(facet.id);
                            else next.add(facet.id);
                            return next;
                          })
                        }
                        className="px-1.5 py-1.5 font-engraved text-[10px] font-semibold uppercase tracking-[0.2em] text-foreground/60 underline underline-offset-4 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                      >
                        {isExpanded ? 'Show fewer' : `Show all ${terms.length}`}
                      </button>
                    )}
                  </div>
                </fieldset>
              );
            })
          )}
        </div>

        <SheetFooter className="flex-row items-center justify-between gap-3 border-t border-foreground/25 p-4">
          <button
            type="button"
            onClick={() => onChange({ ...filters, terms: [] })}
            disabled={termCount === 0}
            className="font-engraved text-[10px] font-semibold uppercase tracking-[0.2em] text-foreground/70 underline underline-offset-4 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:no-underline disabled:opacity-40"
          >
            Clear filters
          </button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded-full bg-foreground px-5 py-2.5 font-engraved text-[11px] font-semibold uppercase tracking-[0.2em] text-background transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            Show {resultCount.toLocaleString()} {resultCount === 1 ? 'record' : 'records'}
          </button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

/* ───────────────────────────────────────────────────────────────────────────
   Applied filters — one slim row under the toolbar, only while a subject filter
   or a search is active. The category itself is already shown (selected) in the
   toolbar, so it isn't repeated here.
   ─────────────────────────────────────────────────────────────────────────── */

interface AppliedFiltersProps {
  filters: ArchiveFilters;
  onChange: (next: ArchiveFilters) => void;
  /** The one full reset (same as "Explore all"): everything back to the default view. */
  onStartOver: () => void;
  resultCount: number;
  totalCount: number;
}

export function AppliedFilters({
  filters,
  onChange,
  onStartOver,
  resultCount,
  totalCount,
}: AppliedFiltersProps) {
  // Anything narrowing the view: a category, a format or subject term, or a
  // search. (Format buttons are ordinary terms, so they show here as chips too.)
  if (!filters.collection && filters.terms.length === 0 && !filters.query.trim()) return null;

  const collection = COLLECTIONS.find((c) => c.id === filters.collection);
  const toggleTerm = (slug: string) =>
    onChange({ ...filters, terms: filters.terms.filter((s) => s !== slug) });

  return (
    <div className="mb-6 flex flex-wrap items-center gap-2 border-b border-foreground/20 pb-3">
      <p
        role="status"
        aria-live="polite"
        className="mr-1 font-mono text-[11px] tabular-nums text-foreground/60"
      >
        {resultCount.toLocaleString()} of {totalCount.toLocaleString()} records
        {collection && <span className="text-foreground/60"> · {collection.title}</span>}
      </p>

      {filters.terms.map((slug) => (
        <AppliedChip
          key={slug}
          facet={termBySlug(slug)?.facet}
          label={termBySlug(slug)?.label ?? slug}
          removeLabel={`Remove filter: ${termBySlug(slug)?.label ?? slug}`}
          onRemove={() => toggleTerm(slug)}
        />
      ))}
      {filters.query.trim() && (
        <AppliedChip
          label={`“${filters.query.trim()}”`}
          removeLabel="Remove search text"
          onRemove={() => onChange({ ...filters, query: '' })}
        />
      )}

      {/* Always offered while anything is narrowing the view, and always the
          same thing: back to everything, exactly like "Explore all". */}
      <button
        type="button"
        onClick={onStartOver}
        className="ml-1 font-engraved text-[10px] font-semibold uppercase tracking-[0.2em] text-foreground/70 underline underline-offset-4 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
      >
        Start over
      </button>
    </div>
  );
}

function AppliedChip({
  label,
  facet,
  removeLabel,
  onRemove,
}: {
  label: string;
  facet?: Facet;
  removeLabel: string;
  onRemove: () => void;
}) {
  const facetLabel = facet ? FACETS.find((f) => f.id === facet)?.label : undefined;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-foreground bg-foreground py-1 pl-3 pr-1 text-background">
      {facetLabel && (
        <span className="hidden font-engraved text-[9px] font-semibold uppercase tracking-[0.16em] text-background/60 sm:inline">
          {facetLabel}
        </span>
      )}
      <span className="font-display text-[15px] leading-none">{label}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={removeLabel}
        className="flex h-5 w-5 items-center justify-center text-background/70 transition-colors hover:text-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-background focus-visible:ring-offset-0"
      >
        <X aria-hidden="true" className="h-3 w-3" />
      </button>
    </span>
  );
}

// Re-exported for callers that need the same notion of "any filter at all".
export { activeFilterCount, hasFilters };
